import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import MobilePageHeader from '../components/MobilePageHeader';
import {
    Dialog,
    DialogContent,
    DialogHeader,
    DialogTitle,
    DialogFooter,
    DialogDescription,
} from '../components/ui/dialog';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '../components/ui/select';
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '../components/ui/table';
import {
    Sheet,
    SheetContent,
    SheetHeader,
    SheetTitle,
    SheetDescription,
} from '../components/ui/sheet';
import { 
    Settings, Gift, Users, History, BarChart3, Award,
    Plus, Edit2, Trash2, Search, Phone, Eye, Save,
    Coffee, Cake, Tag, Utensils, CheckCircle, XCircle,
    TrendingUp, User, CreditCard, RefreshCw,
    ChevronRight, Info, ShoppingCart, Heart
} from 'lucide-react';

// Format currency
const formatCFA = (amount) => {
    return new Intl.NumberFormat('fr-FR').format(amount || 0) + ' F';
};

// Format date
const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
};

const LoyaltyAdminPage = () => {
    const { api, user } = useAuth();
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState('settings');
    
    // Check if user can modify - ONLY ADMIN can modify loyalty program
    // DIRECTEUR and DIRECTEUR_ADJOINT are READ-ONLY
    const role = user?.role || '';
    const canModify = ['ADMIN', 'admin'].includes(role);
    const isReadOnly = !canModify; // Everyone except Admin is read-only
    
    // Program Settings
    const [programSettings, setProgramSettings] = useState({
        enabled: true,
        name: "Le Cercle Aldrin's",
        description: "Programme de fidélité exclusif",
        type: 'amount', // 'visits', 'amount', 'points'
        points_per_visit: 1,
        amount_per_point: 1000,
        only_paid_tickets: true
    });
    const [settingsLoading, setSavingSettings] = useState(false);
    
    // Rewards
    const [rewards, setRewards] = useState([]);
    const [rewardDialogOpen, setRewardDialogOpen] = useState(false);
    const [editingReward, setEditingReward] = useState(null);
    const [rewardForm, setRewardForm] = useState({
        name: '',
        description: '',
        points_required: 10,
        reward_type: 'free_item',
        reward_value: '',
        icon: 'gift',
        is_active: true
    });
    
    // Members
    const [members, setMembers] = useState([]);
    const [memberSearch, setMemberSearch] = useState('');
    const [selectedMember, setSelectedMember] = useState(null);
    const [memberSheetOpen, setMemberSheetOpen] = useState(false);
    const [pointsAdjustment, setPointsAdjustment] = useState({ points: 0, reason: '' });
    
    // History
    const [historyFilter, setHistoryFilter] = useState('all');
    
    // Stats
    const [stats, setStats] = useState(null);

    // Fetch all data
    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const [settingsRes, rewardsRes, membersRes, statsRes] = await Promise.all([
                api().get('/loyalty/settings').catch(() => ({ data: null })),
                api().get('/loyalty/rewards'),
                api().get('/loyalty/members'),
                api().get('/loyalty/stats')
            ]);
            
            if (settingsRes.data) {
                setProgramSettings(prev => ({ ...prev, ...settingsRes.data }));
            }
            setRewards(rewardsRes.data?.rewards || []);
            setMembers(membersRes.data?.members || []);
            setStats(statsRes.data || {});
        } catch (error) {
            console.error('Error fetching data:', error);
            toast.error('Erreur lors du chargement');
        } finally {
            setLoading(false);
        }
    }, [api]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    // Save program settings
    const saveSettings = async () => {
        setSavingSettings(true);
        try {
            await api().post('/loyalty/settings', programSettings);
            toast.success('Paramètres enregistrés');
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        } finally {
            setSavingSettings(false);
        }
    };

    // Reward CRUD
    const openRewardDialog = (reward = null) => {
        if (reward) {
            setEditingReward(reward);
            setRewardForm({
                name: reward.name,
                description: reward.description || '',
                points_required: reward.points_required,
                reward_type: reward.reward_type || 'free_item',
                reward_value: reward.reward_value || '',
                icon: reward.icon || 'gift',
                is_active: reward.is_active !== false
            });
        } else {
            setEditingReward(null);
            setRewardForm({
                name: '',
                description: '',
                points_required: 10,
                reward_type: 'free_item',
                reward_value: '',
                icon: 'gift',
                is_active: true
            });
        }
        setRewardDialogOpen(true);
    };

    const saveReward = async () => {
        if (!rewardForm.name || !rewardForm.points_required) {
            toast.error('Veuillez remplir tous les champs obligatoires');
            return;
        }
        
        try {
            if (editingReward) {
                await api().put(`/loyalty/rewards/${editingReward.id}`, rewardForm);
                toast.success('Récompense modifiée');
            } else {
                await api().post('/loyalty/rewards', rewardForm);
                toast.success('Récompense créée');
            }
            setRewardDialogOpen(false);
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la sauvegarde');
        }
    };

    const deleteReward = async (reward) => {
        if (!window.confirm(`Supprimer "${reward.name}" ?`)) return;
        try {
            await api().delete(`/loyalty/rewards/${reward.id}`);
            toast.success('Récompense supprimée');
            fetchData();
        } catch (error) {
            toast.error('Erreur lors de la suppression');
        }
    };

    // Member functions
    const openMemberSheet = (member) => {
        setSelectedMember(member);
        setPointsAdjustment({ points: 0, reason: '' });
        setMemberSheetOpen(true);
    };

    const adjustMemberPoints = async () => {
        if (!pointsAdjustment.points || !pointsAdjustment.reason) {
            toast.error('Veuillez indiquer les points et la raison');
            return;
        }
        
        try {
            await api().post('/loyalty/manual-points', {
                member_id: selectedMember.id,
                points: pointsAdjustment.points,
                reason: pointsAdjustment.reason
            });
            toast.success(`${pointsAdjustment.points > 0 ? '+' : ''}${pointsAdjustment.points} points appliqués`);
            setPointsAdjustment({ points: 0, reason: '' });
            fetchData();
            // Update selected member
            const updated = await api().get(`/loyalty/member/${selectedMember.id}`);
            setSelectedMember(updated.data);
        } catch (error) {
            toast.error('Erreur lors de la modification');
        }
    };

    // Filter members
    const filteredMembers = members.filter(m => 
        (m.first_name || '').toLowerCase().includes(memberSearch.toLowerCase()) ||
        (m.phone || '').includes(memberSearch)
    );

    // Get reward icon component
    const getRewardIcon = (iconName) => {
        const icons = {
            coffee: Coffee,
            cake: Cake,
            tag: Tag,
            utensils: Utensils,
            gift: Gift
        };
        const IconComponent = icons[iconName] || Gift;
        return <IconComponent className="w-4 h-4" />;
    };

    // Get all history from members
    const allHistory = members.flatMap(m => (m.points_history || []).map(h => ({
        ...h,
        member_name: m.first_name,
        member_phone: m.phone
    }))).sort((a, b) => new Date(b.date) - new Date(a.date));

    const filteredHistory = allHistory.filter(h => {
        if (historyFilter === 'all') return true;
        return h.type === historyFilter;
    });

    if (loading) {
        return (
            <div className="flex items-center justify-center min-h-screen">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-green-600"></div>
            </div>
        );
    }

    return (
        <div className="space-y-4 md:space-y-6 max-w-7xl mx-auto">
            {/* Mobile Header with back button */}
            <MobilePageHeader 
                title="Programme Fidélité" 
                subtitle="Gestion du programme"
                icon={Heart}
            />
            
            {/* Desktop Header - hidden on mobile */}
            <div className="hidden md:flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                <div>
                    <h1 className="text-2xl font-bold flex items-center gap-2">
                        <Gift className="w-7 h-7 text-green-600" />
                        Programme de Fidélité
                    </h1>
                    <p className="text-muted-foreground">
                        Configurez et gérez votre programme de fidélité
                    </p>
                </div>
                <div className="flex gap-2">
                    <Button variant="outline" onClick={fetchData} disabled={loading}>
                        <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
                        Actualiser
                    </Button>
                </div>
            </div>
            
            {/* Mobile Refresh Button */}
            <div className="md:hidden">
                <Button variant="outline" onClick={fetchData} disabled={loading} className="w-full h-12 touch-target">
                    <RefreshCw className={`w-5 h-5 mr-2 ${loading ? 'animate-spin' : ''}`} />
                    Actualiser
                </Button>
            </div>

            {/* Main Tabs */}
            <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
                <TabsList className="grid grid-cols-3 lg:grid-cols-6 gap-1 h-auto p-1">
                    <TabsTrigger value="settings" className="flex items-center gap-2 py-2">
                        <Settings className="w-4 h-4" />
                        <span className="hidden sm:inline">Paramètres</span>
                    </TabsTrigger>
                    <TabsTrigger value="rules" className="flex items-center gap-2 py-2">
                        <Award className="w-4 h-4" />
                        <span className="hidden sm:inline">Règles</span>
                    </TabsTrigger>
                    <TabsTrigger value="rewards" className="flex items-center gap-2 py-2">
                        <Gift className="w-4 h-4" />
                        <span className="hidden sm:inline">Récompenses</span>
                    </TabsTrigger>
                    <TabsTrigger value="members" className="flex items-center gap-2 py-2">
                        <Users className="w-4 h-4" />
                        <span className="hidden sm:inline">Clients</span>
                    </TabsTrigger>
                    <TabsTrigger value="history" className="flex items-center gap-2 py-2">
                        <History className="w-4 h-4" />
                        <span className="hidden sm:inline">Historique</span>
                    </TabsTrigger>
                    <TabsTrigger value="stats" className="flex items-center gap-2 py-2">
                        <BarChart3 className="w-4 h-4" />
                        <span className="hidden sm:inline">Statistiques</span>
                    </TabsTrigger>
                </TabsList>

                {/* ========================================== */}
                {/* BLOC 1: PARAMÈTRES GÉNÉRAUX */}
                {/* ========================================== */}
                <TabsContent value="settings" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Settings className="w-5 h-5 text-green-600" />
                                Paramètres généraux
                            </CardTitle>
                            <CardDescription>
                                Configurez les options principales du programme de fidélité
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Enable/Disable */}
                            <div className="flex items-center justify-between p-4 bg-muted/50 rounded-lg">
                                <div className="flex items-center gap-3">
                                    {programSettings.enabled ? (
                                        <CheckCircle className="w-6 h-6 text-green-600" />
                                    ) : (
                                        <XCircle className="w-6 h-6 text-red-500" />
                                    )}
                                    <div>
                                        <p className="font-medium">Programme de fidélité</p>
                                        <p className="text-sm text-muted-foreground">
                                            {programSettings.enabled ? 'Actif' : 'Désactivé'}
                                        </p>
                                    </div>
                                </div>
                                <Switch
                                    checked={programSettings.enabled}
                                    onCheckedChange={(checked) => 
                                        setProgramSettings({...programSettings, enabled: checked})
                                    }
                                    disabled={!canModify}
                                />
                            </div>

                            {/* Program Name */}
                            <div className="space-y-2">
                                <Label>Nom du programme</Label>
                                <Input
                                    value={programSettings.name}
                                    onChange={(e) => setProgramSettings({...programSettings, name: e.target.value})}
                                    placeholder="Ex: Le Cercle Aldrin's"
                                    disabled={!canModify}
                                />
                            </div>

                            {/* Description */}
                            <div className="space-y-2">
                                <Label>Description (affichée aux clients)</Label>
                                <Textarea
                                    value={programSettings.description}
                                    onChange={(e) => setProgramSettings({...programSettings, description: e.target.value})}
                                    placeholder="Ex: Programme de fidélité exclusif - Gagnez des points à chaque passage !"
                                    rows={2}
                                    disabled={!canModify}
                                />
                            </div>

                            {/* Program Type */}
                            <div className="space-y-2">
                                <Label>Type de programme</Label>
                                <Select 
                                    value={programSettings.type} 
                                    onValueChange={(val) => setProgramSettings({...programSettings, type: val})}
                                    disabled={!canModify}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="visits">
                                            <div className="flex items-center gap-2">
                                                <ShoppingCart className="w-4 h-4" />
                                                Fidélité par passage en caisse
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="amount">
                                            <div className="flex items-center gap-2">
                                                <CreditCard className="w-4 h-4" />
                                                Fidélité par montant dépensé
                                            </div>
                                        </SelectItem>
                                        <SelectItem value="points">
                                            <div className="flex items-center gap-2">
                                                <Award className="w-4 h-4" />
                                                Fidélité par points
                                            </div>
                                        </SelectItem>
                                    </SelectContent>
                                </Select>
                                <p className="text-sm text-muted-foreground">
                                    {programSettings.type === 'visits' && 'Chaque passage en caisse rapporte des points'}
                                    {programSettings.type === 'amount' && 'Les points sont calculés selon le montant dépensé'}
                                    {programSettings.type === 'points' && 'Attribution manuelle des points'}
                                </p>
                            </div>

                            {/* Only paid tickets */}
                            <div className="flex items-center justify-between p-4 border rounded-lg">
                                <div>
                                    <p className="font-medium">Comptabiliser uniquement les tickets payés</p>
                                    <p className="text-sm text-muted-foreground">
                                        Les points sont attribués après le paiement
                                    </p>
                                </div>
                                <Switch
                                    checked={programSettings.only_paid_tickets}
                                    onCheckedChange={(checked) => 
                                        setProgramSettings({...programSettings, only_paid_tickets: checked})
                                    }
                                    disabled={!canModify}
                                />
                            </div>

                            {/* Save Button - Admin only */}
                            {canModify && (
                                <div className="flex justify-end">
                                    <Button onClick={saveSettings} disabled={settingsLoading} className="bg-green-600 hover:bg-green-700">
                                        <Save className="w-4 h-4 mr-2" />
                                        {settingsLoading ? 'Enregistrement...' : 'Enregistrer'}
                                    </Button>
                                </div>
                            )}
                            {isReadOnly && (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                                    Mode lecture seule - Modification réservée à l'administrateur
                                </div>
                            )}
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ========================================== */}
                {/* BLOC 2: RÈGLES DE FIDÉLITÉ */}
                {/* ========================================== */}
                <TabsContent value="rules" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <CardTitle className="flex items-center gap-2">
                                <Award className="w-5 h-5 text-blue-600" />
                                Règles de fidélité
                            </CardTitle>
                            <CardDescription>
                                Définissez comment les points sont attribués
                            </CardDescription>
                        </CardHeader>
                        <CardContent className="space-y-6">
                            {/* Current active rule based on program type */}
                            <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                                <div className="flex items-center gap-2 mb-3">
                                    <CheckCircle className="w-5 h-5 text-green-600" />
                                    <span className="font-medium text-green-800">Règle active</span>
                                </div>
                                {programSettings.type === 'amount' && (
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-white rounded-lg border">
                                            <p className="text-2xl font-bold text-green-600">{formatCFA(programSettings.amount_per_point || 1000)}</p>
                                            <p className="text-sm text-muted-foreground">dépensés</p>
                                        </div>
                                        <ChevronRight className="w-6 h-6 text-muted-foreground" />
                                        <div className="p-3 bg-white rounded-lg border">
                                            <p className="text-2xl font-bold text-amber-600">1</p>
                                            <p className="text-sm text-muted-foreground">point</p>
                                        </div>
                                    </div>
                                )}
                                {programSettings.type === 'visits' && (
                                    <div className="flex items-center gap-4">
                                        <div className="p-3 bg-white rounded-lg border">
                                            <p className="text-2xl font-bold text-green-600">1</p>
                                            <p className="text-sm text-muted-foreground">passage en caisse</p>
                                        </div>
                                        <ChevronRight className="w-6 h-6 text-muted-foreground" />
                                        <div className="p-3 bg-white rounded-lg border">
                                            <p className="text-2xl font-bold text-amber-600">{programSettings.points_per_visit || 1}</p>
                                            <p className="text-sm text-muted-foreground">point(s)</p>
                                        </div>
                                    </div>
                                )}
                                {programSettings.type === 'points' && (
                                    <p className="text-green-700">Points attribués manuellement par le staff</p>
                                )}
                            </div>

                            {/* Configuration for amount type */}
                            {programSettings.type === 'amount' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                                    <div className="space-y-2">
                                        <Label>Montant pour 1 point (FCFA)</Label>
                                        <Input
                                            type="number"
                                            min="100"
                                            step="100"
                                            value={programSettings.amount_per_point || 1000}
                                            onChange={(e) => setProgramSettings({
                                                ...programSettings, 
                                                amount_per_point: parseInt(e.target.value) || 1000
                                            })}
                                            disabled={!canModify}
                                        />
                                    </div>
                                    {canModify && (
                                        <div className="flex items-end">
                                            <Button onClick={saveSettings} variant="outline" className="w-full md:w-auto">
                                                <Save className="w-4 h-4 mr-2" />
                                                Appliquer
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Configuration for visits type */}
                            {programSettings.type === 'visits' && (
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 p-4 border rounded-lg">
                                    <div className="space-y-2">
                                        <Label>Points par passage en caisse</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            value={programSettings.points_per_visit || 1}
                                            onChange={(e) => setProgramSettings({
                                                ...programSettings, 
                                                points_per_visit: parseInt(e.target.value) || 1
                                            })}
                                            disabled={!canModify}
                                        />
                                    </div>
                                    {canModify && (
                                        <div className="flex items-end">
                                            <Button onClick={saveSettings} variant="outline" className="w-full md:w-auto">
                                                <Save className="w-4 h-4 mr-2" />
                                                Appliquer
                                            </Button>
                                        </div>
                                    )}
                                </div>
                            )}

                            {/* Info box */}
                            <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg flex items-start gap-3">
                                <Info className="w-5 h-5 text-blue-600 mt-0.5" />
                                <div className="text-sm text-blue-800">
                                    <p className="font-medium">Intégration automatique avec la caisse</p>
                                    <p>Les points sont automatiquement ajoutés au client lors de la validation du paiement.</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ========================================== */}
                {/* BLOC 3: GESTION DES RÉCOMPENSES */}
                {/* ========================================== */}
                <TabsContent value="rewards" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Gift className="w-5 h-5 text-purple-600" />
                                        Gestion des récompenses
                                    </CardTitle>
                                    <CardDescription>
                                        Créez et gérez les avantages pour vos clients fidèles
                                    </CardDescription>
                                </div>
                                {canModify && (
                                    <Button onClick={() => openRewardDialog()} className="bg-purple-600 hover:bg-purple-700">
                                        <Plus className="w-4 h-4 mr-2" />
                                        Nouvelle récompense
                                    </Button>
                                )}
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                                {rewards.map(reward => (
                                    <Card key={reward.id} className={`${reward.is_active === false ? 'opacity-60' : ''}`}>
                                        <CardContent className="pt-6">
                                            <div className="flex items-start justify-between">
                                                <div className="flex items-center gap-3">
                                                    <div className={`p-3 rounded-full ${reward.is_active !== false ? 'bg-purple-100' : 'bg-gray-100'}`}>
                                                        {getRewardIcon(reward.icon)}
                                                    </div>
                                                    <div>
                                                        <p className="font-medium">{reward.name}</p>
                                                        <Badge variant="outline" className="mt-1">
                                                            {reward.points_required} points
                                                        </Badge>
                                                    </div>
                                                </div>
                                                {canModify && (
                                                    <div className="flex gap-1">
                                                        <Button variant="ghost" size="sm" onClick={() => openRewardDialog(reward)}>
                                                            <Edit2 className="w-4 h-4" />
                                                        </Button>
                                                        <Button variant="ghost" size="sm" onClick={() => deleteReward(reward)} className="text-red-600">
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </div>
                                                )}
                                            </div>
                                            {reward.description && (
                                                <p className="text-sm text-muted-foreground mt-3">{reward.description}</p>
                                            )}
                                            <div className="mt-3">
                                                <Badge className={reward.is_active !== false ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}>
                                                    {reward.is_active !== false ? 'Actif' : 'Inactif'}
                                                </Badge>
                                            </div>
                                        </CardContent>
                                    </Card>
                                ))}
                                
                                {rewards.length === 0 && (
                                    <Card className="col-span-full">
                                        <CardContent className="py-12 text-center">
                                            <Gift className="w-12 h-12 mx-auto text-gray-300 mb-4" />
                                            <p className="text-muted-foreground">Aucune récompense configurée</p>
                                            {canModify && (
                                                <Button className="mt-4" onClick={() => openRewardDialog()}>
                                                    <Plus className="w-4 h-4 mr-2" />
                                                    Créer une récompense
                                                </Button>
                                            )}
                                        </CardContent>
                                    </Card>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ========================================== */}
                {/* BLOC 4: CLIENTS INSCRITS */}
                {/* ========================================== */}
                <TabsContent value="members" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <Users className="w-5 h-5 text-blue-600" />
                                        Clients inscrits ({members.length})
                                    </CardTitle>
                                    <CardDescription>
                                        Gérez les membres du programme de fidélité
                                    </CardDescription>
                                </div>
                                <div className="relative w-full sm:w-64">
                                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                                    <Input
                                        placeholder="Rechercher (nom, téléphone)"
                                        value={memberSearch}
                                        onChange={(e) => setMemberSearch(e.target.value)}
                                        className="pl-10"
                                    />
                                </div>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="overflow-x-auto">
                                <Table>
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead>Client</TableHead>
                                            <TableHead>Téléphone</TableHead>
                                            <TableHead className="text-center">Passages</TableHead>
                                            <TableHead className="text-center">Points</TableHead>
                                            <TableHead>Récompenses dispo.</TableHead>
                                            <TableHead className="text-right">Actions</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {filteredMembers.length === 0 ? (
                                            <TableRow>
                                                <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">
                                                    {memberSearch ? 'Aucun client trouvé' : 'Aucun client inscrit'}
                                                </TableCell>
                                            </TableRow>
                                        ) : (
                                            filteredMembers.map(member => {
                                                const availableRewards = rewards.filter(r => r.points_required <= member.points && r.is_active !== false);
                                                return (
                                                    <TableRow key={member.id}>
                                                        <TableCell>
                                                            <div className="flex items-center gap-2">
                                                                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center">
                                                                    <User className="w-4 h-4 text-green-600" />
                                                                </div>
                                                                <span className="font-medium">{member.first_name}</span>
                                                            </div>
                                                        </TableCell>
                                                        <TableCell>
                                                            <div className="flex items-center gap-1 text-muted-foreground">
                                                                <Phone className="w-3 h-3" />
                                                                {member.phone}
                                                            </div>
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <Badge variant="outline">{member.visits || 0}</Badge>
                                                        </TableCell>
                                                        <TableCell className="text-center">
                                                            <span className="font-bold text-amber-600">{member.points}</span>
                                                        </TableCell>
                                                        <TableCell>
                                                            {availableRewards.length > 0 ? (
                                                                <div className="flex gap-1">
                                                                    {availableRewards.slice(0, 2).map(r => (
                                                                        <Badge key={r.id} className="bg-purple-100 text-purple-700">
                                                                            {getRewardIcon(r.icon)}
                                                                        </Badge>
                                                                    ))}
                                                                    {availableRewards.length > 2 && (
                                                                        <Badge variant="outline">+{availableRewards.length - 2}</Badge>
                                                                    )}
                                                                </div>
                                                            ) : (
                                                                <span className="text-sm text-muted-foreground">-</span>
                                                            )}
                                                        </TableCell>
                                                        <TableCell className="text-right">
                                                            <Button variant="outline" size="sm" onClick={() => openMemberSheet(member)}>
                                                                <Eye className="w-4 h-4 mr-1" />
                                                                Détail
                                                            </Button>
                                                        </TableCell>
                                                    </TableRow>
                                                );
                                            })
                                        )}
                                    </TableBody>
                                </Table>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ========================================== */}
                {/* BLOC 5: HISTORIQUE */}
                {/* ========================================== */}
                <TabsContent value="history" className="space-y-6">
                    <Card>
                        <CardHeader>
                            <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                                <div>
                                    <CardTitle className="flex items-center gap-2">
                                        <History className="w-5 h-5 text-orange-600" />
                                        Historique du programme
                                    </CardTitle>
                                    <CardDescription>
                                        Journal des actions du programme de fidélité
                                    </CardDescription>
                                </div>
                                <Select value={historyFilter} onValueChange={setHistoryFilter}>
                                    <SelectTrigger className="w-40">
                                        <SelectValue placeholder="Filtrer" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="all">Tout</SelectItem>
                                        <SelectItem value="purchase">Passages</SelectItem>
                                        <SelectItem value="redemption">Récompenses</SelectItem>
                                        <SelectItem value="manual">Ajustements</SelectItem>
                                        <SelectItem value="bonus">Bonus</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </CardHeader>
                        <CardContent>
                            <div className="space-y-3">
                                {filteredHistory.slice(0, 50).map((item, index) => (
                                    <div key={index} className="flex items-center gap-4 p-3 border rounded-lg hover:bg-muted/50">
                                        <div className={`p-2 rounded-full ${
                                            item.type === 'purchase' ? 'bg-green-100' :
                                            item.type === 'redemption' ? 'bg-purple-100' :
                                            item.type === 'bonus' ? 'bg-amber-100' :
                                            'bg-blue-100'
                                        }`}>
                                            {item.type === 'purchase' ? <ShoppingCart className="w-4 h-4 text-green-600" /> :
                                             item.type === 'redemption' ? <Gift className="w-4 h-4 text-purple-600" /> :
                                             item.type === 'bonus' ? <Award className="w-4 h-4 text-amber-600" /> :
                                             <Edit2 className="w-4 h-4 text-blue-600" />}
                                        </div>
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2">
                                                <span className="font-medium">{item.member_name}</span>
                                                <span className="text-xs text-muted-foreground">{item.member_phone}</span>
                                            </div>
                                            <p className="text-sm text-muted-foreground truncate">{item.description}</p>
                                        </div>
                                        <div className="text-right">
                                            <p className={`font-bold ${item.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {item.points > 0 ? '+' : ''}{item.points} pts
                                            </p>
                                            <p className="text-xs text-muted-foreground">{formatDate(item.date)}</p>
                                        </div>
                                    </div>
                                ))}
                                
                                {filteredHistory.length === 0 && (
                                    <div className="text-center py-12 text-muted-foreground">
                                        <History className="w-12 h-12 mx-auto mb-4 opacity-50" />
                                        <p>Aucun historique disponible</p>
                                    </div>
                                )}
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>

                {/* ========================================== */}
                {/* BLOC 6: STATISTIQUES */}
                {/* ========================================== */}
                <TabsContent value="stats" className="space-y-6">
                    <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-blue-100 rounded-full">
                                        <Users className="w-6 h-6 text-blue-600" />
                                    </div>
                                    <div>
                                        <p className="text-3xl font-bold">{stats?.total_members || 0}</p>
                                        <p className="text-sm text-muted-foreground">Clients inscrits</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-green-100 rounded-full">
                                        <TrendingUp className="w-6 h-6 text-green-600" />
                                    </div>
                                    <div>
                                        <p className="text-2xl font-bold">{formatCFA(stats?.total_revenue_from_members || 0)}</p>
                                        <p className="text-sm text-muted-foreground">CA membres fidélité</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-amber-100 rounded-full">
                                        <Award className="w-6 h-6 text-amber-600" />
                                    </div>
                                    <div>
                                        <p className="text-3xl font-bold">{stats?.total_points_in_circulation || 0}</p>
                                        <p className="text-sm text-muted-foreground">Points en circulation</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                        
                        <Card>
                            <CardContent className="pt-6">
                                <div className="flex items-center gap-4">
                                    <div className="p-3 bg-purple-100 rounded-full">
                                        <Gift className="w-6 h-6 text-purple-600" />
                                    </div>
                                    <div>
                                        <p className="text-3xl font-bold">{stats?.total_rewards_redeemed || 0}</p>
                                        <p className="text-sm text-muted-foreground">Récompenses utilisées</p>
                                    </div>
                                </div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Members by status */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Répartition par statut</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-3 gap-4">
                                <div className="p-4 bg-amber-50 rounded-lg text-center border border-amber-200">
                                    <p className="text-3xl font-bold text-amber-700">{stats?.members_by_status?.bronze || 0}</p>
                                    <p className="text-sm text-amber-600">Bronze</p>
                                </div>
                                <div className="p-4 bg-gray-50 rounded-lg text-center border border-gray-200">
                                    <p className="text-3xl font-bold text-gray-600">{stats?.members_by_status?.silver || 0}</p>
                                    <p className="text-sm text-gray-500">Argent</p>
                                </div>
                                <div className="p-4 bg-yellow-50 rounded-lg text-center border border-yellow-200">
                                    <p className="text-3xl font-bold text-yellow-600">{stats?.members_by_status?.gold || 0}</p>
                                    <p className="text-sm text-yellow-500">Or</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>

                    {/* Activity stats */}
                    <Card>
                        <CardHeader>
                            <CardTitle>Activité</CardTitle>
                        </CardHeader>
                        <CardContent>
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                                <div className="p-4 border rounded-lg text-center">
                                    <p className="text-2xl font-bold">{stats?.total_visits || 0}</p>
                                    <p className="text-sm text-muted-foreground">Passages total</p>
                                </div>
                                <div className="p-4 border rounded-lg text-center">
                                    <p className="text-2xl font-bold">
                                        {stats?.total_members ? Math.round((stats.total_visits || 0) / stats.total_members * 10) / 10 : 0}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Moy. passages/client</p>
                                </div>
                                <div className="p-4 border rounded-lg text-center">
                                    <p className="text-2xl font-bold">
                                        {stats?.total_members ? formatCFA(Math.round((stats.total_revenue_from_members || 0) / stats.total_members)) : '0 F'}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Panier moyen</p>
                                </div>
                                <div className="p-4 border rounded-lg text-center">
                                    <p className="text-2xl font-bold">
                                        {rewards.filter(r => r.is_active !== false).length}
                                    </p>
                                    <p className="text-sm text-muted-foreground">Récompenses actives</p>
                                </div>
                            </div>
                        </CardContent>
                    </Card>
                </TabsContent>
            </Tabs>

            {/* ========================================== */}
            {/* MODALS */}
            {/* ========================================== */}

            {/* Reward Dialog */}
            <Dialog open={rewardDialogOpen} onOpenChange={setRewardDialogOpen}>
                <DialogContent className="max-w-md">
                    <DialogHeader>
                        <DialogTitle>
                            {editingReward ? 'Modifier la récompense' : 'Nouvelle récompense'}
                        </DialogTitle>
                        <DialogDescription>
                            Définissez les détails de la récompense
                        </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4 py-4">
                        <div className="space-y-2">
                            <Label>Nom de la récompense *</Label>
                            <Input
                                value={rewardForm.name}
                                onChange={(e) => setRewardForm({...rewardForm, name: e.target.value})}
                                placeholder="Ex: Café offert, Dessert gratuit..."
                            />
                        </div>
                        
                        <div className="space-y-2">
                            <Label>Description</Label>
                            <Textarea
                                value={rewardForm.description}
                                onChange={(e) => setRewardForm({...rewardForm, description: e.target.value})}
                                placeholder="Description affichée au client"
                                rows={2}
                            />
                        </div>
                        
                        <div className="grid grid-cols-2 gap-4">
                            <div className="space-y-2">
                                <Label>Points requis *</Label>
                                <Input
                                    type="number"
                                    min="1"
                                    value={rewardForm.points_required}
                                    onChange={(e) => setRewardForm({...rewardForm, points_required: parseInt(e.target.value) || 1})}
                                />
                            </div>
                            <div className="space-y-2">
                                <Label>Icône</Label>
                                <Select value={rewardForm.icon} onValueChange={(val) => setRewardForm({...rewardForm, icon: val})}>
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="coffee">☕ Café</SelectItem>
                                        <SelectItem value="cake">🍰 Dessert</SelectItem>
                                        <SelectItem value="utensils">🍽️ Plat</SelectItem>
                                        <SelectItem value="tag">🏷️ Réduction</SelectItem>
                                        <SelectItem value="gift">🎁 Cadeau</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>
                        </div>
                        
                        <div className="flex items-center justify-between p-3 border rounded-lg">
                            <div>
                                <p className="font-medium">Récompense active</p>
                                <p className="text-sm text-muted-foreground">Visible pour les clients</p>
                            </div>
                            <Switch
                                checked={rewardForm.is_active}
                                onCheckedChange={(checked) => setRewardForm({...rewardForm, is_active: checked})}
                            />
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setRewardDialogOpen(false)}>
                            Annuler
                        </Button>
                        <Button onClick={saveReward} className="bg-purple-600 hover:bg-purple-700">
                            {editingReward ? 'Modifier' : 'Créer'}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>

            {/* Member Detail Sheet */}
            <Sheet open={memberSheetOpen} onOpenChange={setMemberSheetOpen}>
                <SheetContent className="w-full sm:max-w-lg overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle>Détail du client</SheetTitle>
                        <SheetDescription>
                            Consultez et modifiez les informations du membre
                        </SheetDescription>
                    </SheetHeader>
                    
                    {selectedMember && (
                        <div className="mt-6 space-y-6">
                            {/* Member info */}
                            <div className="flex items-center gap-4 p-4 bg-green-50 rounded-lg">
                                <div className="w-16 h-16 bg-green-600 rounded-full flex items-center justify-center">
                                    <User className="w-8 h-8 text-white" />
                                </div>
                                <div>
                                    <p className="text-xl font-bold">{selectedMember.first_name}</p>
                                    <p className="text-muted-foreground flex items-center gap-1">
                                        <Phone className="w-3 h-3" />
                                        {selectedMember.phone}
                                    </p>
                                    <Badge className="mt-1 bg-amber-100 text-amber-700">
                                        {selectedMember.status || 'Bronze'}
                                    </Badge>
                                </div>
                            </div>
                            
                            {/* Stats */}
                            <div className="grid grid-cols-3 gap-3">
                                <div className="p-3 border rounded-lg text-center">
                                    <p className="text-2xl font-bold text-amber-600">{selectedMember.points}</p>
                                    <p className="text-xs text-muted-foreground">Points</p>
                                </div>
                                <div className="p-3 border rounded-lg text-center">
                                    <p className="text-2xl font-bold">{selectedMember.visits || 0}</p>
                                    <p className="text-xs text-muted-foreground">Passages</p>
                                </div>
                                <div className="p-3 border rounded-lg text-center">
                                    <p className="text-lg font-bold">{formatCFA(selectedMember.total_spent || 0)}</p>
                                    <p className="text-xs text-muted-foreground">Total dépensé</p>
                                </div>
                            </div>
                            
                            {/* Available rewards */}
                            <div>
                                <h4 className="font-medium mb-2">Récompenses disponibles</h4>
                                <div className="space-y-2">
                                    {rewards.filter(r => r.points_required <= selectedMember.points && r.is_active !== false).map(r => (
                                        <div key={r.id} className="flex items-center justify-between p-2 bg-purple-50 rounded border border-purple-200">
                                            <div className="flex items-center gap-2">
                                                {getRewardIcon(r.icon)}
                                                <span>{r.name}</span>
                                            </div>
                                            <Badge variant="outline">{r.points_required} pts</Badge>
                                        </div>
                                    ))}
                                    {rewards.filter(r => r.points_required <= selectedMember.points && r.is_active !== false).length === 0 && (
                                        <p className="text-sm text-muted-foreground text-center py-4">
                                            Aucune récompense disponible
                                        </p>
                                    )}
                                </div>
                            </div>
                            
                            {/* Points adjustment - Admin only */}
                            {canModify ? (
                                <div className="p-4 border rounded-lg space-y-3">
                                    <h4 className="font-medium">Ajuster les points</h4>
                                    <div className="grid grid-cols-2 gap-3">
                                        <div className="space-y-1">
                                            <Label className="text-xs">Points (+/-)</Label>
                                            <Input
                                                type="number"
                                                value={pointsAdjustment.points}
                                                onChange={(e) => setPointsAdjustment({...pointsAdjustment, points: parseInt(e.target.value) || 0})}
                                                placeholder="Ex: 5 ou -3"
                                            />
                                        </div>
                                        <div className="space-y-1">
                                            <Label className="text-xs">Raison</Label>
                                            <Input
                                                value={pointsAdjustment.reason}
                                                onChange={(e) => setPointsAdjustment({...pointsAdjustment, reason: e.target.value})}
                                                placeholder="Ex: Correction"
                                            />
                                        </div>
                                    </div>
                                    <Button 
                                        onClick={adjustMemberPoints} 
                                        className="w-full"
                                        disabled={!pointsAdjustment.points || !pointsAdjustment.reason}
                                    >
                                        Appliquer l'ajustement
                                    </Button>
                                </div>
                            ) : (
                                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-700">
                                    Mode lecture seule - Ajustement des points réservé à l'administrateur
                                </div>
                            )}
                            
                            {/* History */}
                            <div>
                                <h4 className="font-medium mb-2">Historique récent</h4>
                                <div className="space-y-2 max-h-60 overflow-y-auto">
                                    {(selectedMember.points_history || []).slice(-10).reverse().map((h, i) => (
                                        <div key={i} className="flex items-center justify-between p-2 text-sm border-b">
                                            <div>
                                                <p className="text-muted-foreground">{h.description}</p>
                                                <p className="text-xs text-muted-foreground">{formatDate(h.date)}</p>
                                            </div>
                                            <span className={`font-bold ${h.points > 0 ? 'text-green-600' : 'text-red-600'}`}>
                                                {h.points > 0 ? '+' : ''}{h.points}
                                            </span>
                                        </div>
                                    ))}
                                </div>
                            </div>
                        </div>
                    )}
                </SheetContent>
            </Sheet>
        </div>
    );
};

export default LoyaltyAdminPage;
